import { Map, List, fromJS, OrderedMap } from 'immutable';
import { RowTypes } from 'flux/stores/cuesheet/CuesheetConstants.js';
import {
  ColumnWidthClassNames,
  EditableCellTypes,
  DefaultShowColumnOrder,
  DefaultGEColumnOrder,
  DefaultGridColumnOrder,
} from 'flux/stores/cuesheet/CuesheetConstants';
import {
  calculateDerivedFieldsForRows,
  getTotalDurationsAndTotalActualDurations,
} from '@shoflo-rundown/cuesheet-data-helper';
import { isDisplayView } from 'main/components/show/cuesheet/cuesheetViewHelper';
import TimeDisplayTypes from 'shared/constants/TimeDisplayTypes';
import { DisplayTypes } from 'shared/constants/FlowConstants';

import _ from 'underscore';

export default {
  /**
   *
   * Create an immutable representation of the item set list data
   *
   * TODO: test perf on large sets...fromJS() could be an issue..
   *
   */
  /**
   * Build the plain `item` object stored at a row's rowMap path from a fetched
   * row (see getRowPath). Shared by the initial load (createNestedMap) and the
   * failed-save revert (_applyRefetchedRows) so the row->item shape stays in one
   * place. Does NOT set `globalElement` — callers apply that (lookup on load,
   * preserve-existing on revert).
   * See https://github.com/SHOFLO/Issues/issues/2461 for the null-item case.
   */
  buildRowItem(row) {
    const item = row.item[0] || { cells: {} };
    item.titleView = row.titleRow;
    item.labels = row.labels;
    return item;
  },

  createNestedMap(itemSetList, globalElementShowItems) {
    let globalElementRowIdLookup = {};

    globalElementShowItems.forEach((ge) => {
      globalElementRowIdLookup[ge.row] = ge;
    });

    const nestedMap = Map().asMutable();

    itemSetList.itemSets.forEach((itemSet) => {
      const itemSetId = itemSet._id;
      //TODO: add back when we do segments/set titles
      //nestedMap.setIn(this.getItemSetPath(itemSetId), fromJS(_.pick(itemSet, "title")));
      let itemIds = [];

      itemSet.rows.forEach((row) => {
        const rowId = row._id;
        itemIds.push(rowId);
        const item = this.buildRowItem(row);
        item.globalElement = globalElementRowIdLookup[rowId]
          ? globalElementRowIdLookup[rowId]
          : false;
        nestedMap.setIn(this.getRowPath(itemSetId, rowId), fromJS(item));
        row.subItems.forEach((subItem) => {
          const subItemId = subItem._id;
          nestedMap.setIn(
            this.getSubItemPath(itemSetId, rowId, subItemId),
            fromJS(subItem),
          );
        });
      });
    });

    return nestedMap.asImmutable();
  },

  /**
   * Create an index that maps a row position to a nested tree structure
   *
   */
  createRowIndex(itemSetList, map) {
    const rowIndex = List().asMutable();

    itemSetList.itemSets.forEach((itemSet) => {
      const itemSetId = itemSet._id;
      //TODO: add back when we do segments/set titles
      //rowIndex.push(Map({type: RowTypes.ItemSetTitle, path: this.getItemSetPath(itemSetId)}));
      this._createRowIndexFromRowOrder(
        itemSetId,
        itemSet.rowOrder,
        map,
        rowIndex,
      );
    });

    return rowIndex.asImmutable();
  },

  createRowIndexFromRowOrder(itemSetId, rowOrder, map) {
    const rowIndex = List().asMutable();

    this._createRowIndexFromRowOrder(itemSetId, rowOrder, map, rowIndex);

    return rowIndex.asImmutable();
  },

  _createRowIndexFromRowOrder(itemSetId, rowOrder, map, rowIndexMutable) {
    rowOrder.forEach((rowId, index) => {
      const rowPath = this.getRowPath(itemSetId, rowId);
      let item = map.getIn(rowPath);
      if (!item) {
        //in DisplayView we return because we only load rows as needed and any unloaded rows
        //will be missing from the row map and trigger the error
        if (isDisplayView()) return;
        const errorMessage = `${rowId} row doesn't exist in itemSet ${itemSetId} at position ${index}! This is related to https://github.com/SHOFLO/Issues/issues/2915`;
        if (console.debug) console.debug(errorMessage);
        return;
      }
      item._id = rowId;
      const type = item.get('titleView') ? RowTypes.TitleItem : RowTypes.Item;
      rowIndexMutable.push(this.getRowSpec(type, rowPath));

      //We don't have subItems, not sure if we ever will..commenting
      // if (!rowId.itemOrder) return;
      // rowId.itemOrder.forEach(subItem => {
      //   const subItemId = subItem._id;
      //   rowIndexMutable.push(Map({type: RowTypes.SubItem, path: this.getSubItemPath(itemSetId, rowId, subItemId)}));
      // });
    });
  },

  /**
   * Get column order
   *
   * What's happening?
   *
   * 1. Get the order...use user order if present, otherwise default order
   * 2. For each hidden column, delete it from the order (assuming it exists in the order at all)
   *
   */
  getColumnOrder(itemSetList, userId) {
    const order = this.getFullColumnOrder(itemSetList, userId).toArray();
    itemSetList.columns.forEach(function (col) {
      if (
        col.userSettings &&
        col.userSettings[userId] &&
        col.userSettings[userId].hidden
      ) {
        const indx = order.indexOf(col._id);
        if (indx >= 0) {
          order.splice(indx, 1);
        }
      }
    });

    return List(order);
  },

  /**
   * Get column order
   *
   * What's happening?
   *
   * 1. Get the order...use user order if present, otherwise default order
   * 2. For each hidden column, delete it from the order (assuming it exists in the order at all)
   *
   */
  getGuestColumnOrder(itemSetList) {
    const columnOrder =
      itemSetList.columnOrders && itemSetList.columnOrders.guestPass
        ? itemSetList.columnOrders.guestPass
        : itemSetList.defaultColumnOrder;

    return List(columnOrder);
  },

  getSpecialColumnsForGuestAndPrintable(state, exportColumnOrder) {
    const show = state.get('show');

    if (!show) return OrderedMap();

    const displayType = show.get('displayType');
    const settings = state.get('settings');
    const userSettings = state.get('userSettings');

    let columnOrder = DefaultShowColumnOrder;

    if (
      displayType === DisplayTypes.schedule ||
      displayType === DisplayTypes.list
    ) {
      columnOrder = DefaultGridColumnOrder;
    } else if (displayType === DisplayTypes.globalElementContainer) {
      columnOrder = DefaultGEColumnOrder;
    } else if (exportColumnOrder) {
      columnOrder = exportColumnOrder;
    }

    let itemTitleWidth = ColumnWidthClassNames.Narrow;
    let itemDescriptionWidth = ColumnWidthClassNames.Narrow;
    let noteColumnWidth = ColumnWidthClassNames.Narrow;

    /*
    settings.getIn(['itemColumn', 'width']) is used for cue item width
    settings.getIn(['itemTitleColumn', 'width']) is used to get the grid title column width
    settings.getIn(['itemDescriptionColumn', 'width']) is used to get the grid description column width
    */

    if (settings && settings.hasIn(['itemColumn', 'width'])) {
      itemTitleWidth = settings.getIn(['itemColumn', 'width']);
    }

    if (settings && settings.hasIn(['itemTitleColumn', 'width'])) {
      itemTitleWidth = settings.getIn(['itemTitleColumn', 'width']);
    }

    if (settings && settings.hasIn(['itemDescriptionColumn', 'width'])) {
      itemDescriptionWidth = settings.getIn(['itemDescriptionColumn', 'width']);
    }

    if (userSettings && userSettings.hasIn(['noteColumn', 'width'])) {
      noteColumnWidth = userSettings.getIn(['noteColumn', 'width']);
    }

    const presetDurationDisplayType = show.get('presetDurationDisplayType');
    let presetTitle = 'Preset';

    if (presetDurationDisplayType === TimeDisplayTypes.clockTime) {
      presetTitle = 'Clock Time';
    }

    let specialColumns = OrderedMap();

    function setTimingColumns(colId) {
      if (
        colId === EditableCellTypes.ItemStartTime &&
        show.get('displayStartTime')
      ) {
        specialColumns = specialColumns.set(
          EditableCellTypes.ItemStartTime,
          Map({
            classes: 'cs-start-time-header',
            title: 'Start',
            hidden: false,
          }),
        );
      }
      if (
        colId === EditableCellTypes.ItemPresetDuration &&
        show.get('displayPresetDuration')
      ) {
        specialColumns = specialColumns.set(
          EditableCellTypes.ItemPresetDuration,
          Map({
            classes: 'cs-preset-header',
            title: presetTitle,
            hidden: false,
          }),
        );
      }
      if (
        colId === EditableCellTypes.ItemDuration &&
        show.get('displayDuration')
      ) {
        specialColumns = specialColumns.set(
          EditableCellTypes.ItemDuration,
          Map({
            classes: 'cs-duration-header',
            title: 'Duration',
            hidden: false,
          }),
        );
      }
    }

    columnOrder.forEach((colId) => {
      if (
        colId === EditableCellTypes.ItemNumber &&
        show.get('displayItemNumbers')
      ) {
        specialColumns = specialColumns.set(
          EditableCellTypes.ItemNumber,
          Map({ classes: 'cs-number-header', title: '#', hidden: false }),
        );
      }

      if (
        displayType === DisplayTypes.schedule ||
        displayType === DisplayTypes.list
      ) {
        setTimingColumns(colId);
      }

      if (
        colId === EditableCellTypes.ItemTitle &&
        show.get('displayItemTitles')
      ) {
        specialColumns = specialColumns.set(
          EditableCellTypes.ItemTitle,
          Map({
            classes: 'cs-item-header',
            title: 'Item',
            hidden: false,
            width: itemTitleWidth,
          }),
        );
      }
      if (
        colId === EditableCellTypes.ItemDescription &&
        show.get('displayItemDescriptions')
      ) {
        specialColumns = specialColumns.set(
          EditableCellTypes.ItemDescription,
          Map({
            classes: 'cs-desc-header',
            title: 'Description',
            hidden: false,
            width: itemDescriptionWidth,
          }),
        );
      }

      if (
        displayType === DisplayTypes.show ||
        displayType === DisplayTypes.globalElementContainer
      ) {
        setTimingColumns(colId);
      }

      if (colId === EditableCellTypes.ItemNote) {
        specialColumns = specialColumns.set(
          EditableCellTypes.ItemNote,
          Map({
            classes: 'cs-notes-header',
            title: 'Private Notes',
            hidden: false,
            width: noteColumnWidth,
          }),
        );
      }
    });

    return specialColumns;
  },

  /**
   * Get the order, including hidden columns
   */
  getFullColumnOrder(itemSetList, userId) {
    return List(
      itemSetList.userColumnOrder[userId] || itemSetList.defaultColumnOrder,
    );
  },

  _getFirstItemNumber(state) {
    const defaultItemNumber = 100;
    const firstRowSpec = state.getIn(['rows', 0]);
    const firstItem = state.getIn(['rowMap'].concat(firstRowSpec.get('path')));

    let num = parseInt(firstItem.get('number'), 10);
    if (isNaN(num)) num = defaultItemNumber;
    return num;
  },

  /**
   * Change startTime and presetTime values to be based on duration offsets
   */
  getStateWithDerivedCalculations(state) {
    const rowOrder = this._getRowOrderFromState(state);

    /**
     * Get the derived/adjusted fields
     */
    const rowHash = calculateDerivedFieldsForRows(
      state.get('show').toJS(),
      rowOrder,
      state,
      /**
       * Pure getter that gets the row from the state, given a rowId
       */
      (rowId, rowIndex, state) => {
        const path = ['rowMap'].concat(state.getIn(['rows', rowIndex, 'path']));
        return state.getIn(path).toJS();
      },
    );

    return state.withMutations((state) => {
      const rows = state.get('rows');

      /**
       * Iterate throw each row in the row order
       */
      rows.forEach((rowSpec) => {
        const path = ['rowMap'].concat(rowSpec.get('path'));

        state.updateIn(path, (row) => {
          //Get the rowId from the 'path' variable
          const rowId = path[3];
          //Get the adjusted values for the rowId
          const adjustedValues = rowHash[rowId];
          //Build the object to use to mutate the item
          const assignValues = {};
          _.each(adjustedValues, (value, key) => {
            assignValues[key + 'Adjusted'] = value;
          });

          //Assign the 'adjusted' values to the object
          return fromJS(Object.assign(row.toJS(), assignValues));
        });
      });
    });
  },

  getStateWithTotalDurations(state) {
    const rowOrder = this._getRowOrderFromState(state);

    const totalDurations = getTotalDurationsAndTotalActualDurations(
      state,
      rowOrder,
      (rowId, rowIndex, state) => {
        const path = ['rowMap'].concat(state.getIn(['rows', rowIndex, 'path']));
        return state.getIn(path).toJS();
      },
    );

    const { totalDurationInSeconds, totalActualDurationInSeconds } =
      totalDurations;

    return state
      .set('totalDurationInSeconds', totalDurationInSeconds)
      .set('totalActualDurationInSeconds', totalActualDurationInSeconds);
  },

  _getRowOrderFromState(state) {
    return state
      .get('rows')
      .toJS()
      .map((rowSpec) => rowSpec.path[2]);
  },

  /**
   * Get columns sorted, using the order. If the column isn't in the order, it won't be in the returned map
   */
  getSortedColumns(columns, order) {
    return OrderedMap(
      order.map((colId) => [
        colId,
        Map(_.find(columns, (col) => col._id === colId)),
      ]),
    );
  },

  /**
   * Rebuild the sorted columns with a an order
   */
  rebuildSortedColumns(columnOrderedMap, order) {
    return OrderedMap(
      order.map((colId) => [colId, columnOrderedMap.get(colId)]),
    );
  },

  /**
   * Create a row spec. A rowSpec consists of the type of row (titleItem, item, subItem, etc) and a path to the data for the row
   */
  getRowSpec(type, path) {
    return Map({ type, path });
  },

  /**
   * Build a key path for item set data
   */
  getItemSetPath(itemSetId) {
    return [itemSetId, 'fields'];
  },

  /**
   * Build a key path for item row data
   */
  getRowPath(itemSetId, rowId) {
    return [itemSetId, 'rows', rowId, 'item'];
  },

  /**
   * Build a key path for sub item data
   */
  getSubItemPath(itemSetId, rowId, subItemId) {
    return [itemSetId, 'rows', rowId, 'subItems', subItemId, 'item'];
  },

  /**
   * Find a row ID, given an index
   * @param {*} rowIndex
   * @param {*} state
   */
  findRowId(rowIndex, state) {
    return state.getIn(['rows', rowIndex, 'path'])[2];
  },
};
